import { useEffect, useMemo, useState } from "react";
import { ImageOff } from "lucide-react";
import { api } from "@/lib/api";
import { ENV } from "@/lib/environment";
import { feedImage, blobMatches } from "@/lib/media";
import { memberToken } from "@/lib/memberToken";
import { useI18n } from "@/i18n/I18nContext";

// Shared by feed and post views. A member token is never placed in an image URL.
export function ProtectedImage({ raw, testid }) {
  const { t } = useI18n();
  const img = useMemo(() => feedImage(raw, ENV.core), [raw]);
  const [attempt, retry] = useState(0);
  const [state, setState] = useState(null);
  useEffect(() => {
    setState(null);
    if (!img || !img.alt) return undefined;
    const ctrl = new AbortController();
    const version = memberToken.getSessionVersion();
    let active = true;
    let url = null;
    const current = () => active && !ctrl.signal.aborted && memberToken.getSessionVersion() === version;
    const release = () => { if (url) { URL.revokeObjectURL(url); url = null; } };
    const timer = setTimeout(() => {
      if (!current()) return;
      setState({ img, version, failed: true });
      ctrl.abort();
    }, 15000);
    const stop = () => { active = false; clearTimeout(timer); ctrl.abort(); release(); };
    const unsubscribe = memberToken.onEnd(() => { stop(); setState(null); });
    api.get(img.path, { responseType: "blob", signal: ctrl.signal })
      .then((r) => {
        if (!current()) return;
        if (!(r.data instanceof Blob) || !blobMatches(r.data, img.mime) || r.data.size === 0 || r.data.size > 2 * 1024 * 1024) throw new Error("invalid image");
        url = URL.createObjectURL(r.data);
        setState({ img, version, src: url, release, failed: false });
      })
      .catch((error) => {
        if (!current()) return;
        setState(error?.response?.status === 404 ? null : { img, version, failed: true });
      })
      .finally(() => clearTimeout(timer));
    return () => { stop(); unsubscribe(); };
  }, [img, attempt]);

  if (!img) return null;
  if (!img.alt) return <p className="text-base leading-relaxed text-v3-muted" data-testid={testid}>Bild nicht angezeigt – die Bildbeschreibung fehlt.</p>;
  if (state?.img !== img || state.version !== memberToken.getSessionVersion()) return null;
  if (state.failed) return (
    <figure className="mt-4 space-y-2 rounded-xl border border-v3-line p-4" data-testid={testid} data-state="unavailable">
      <figcaption className="space-y-2" data-testid={`${testid}-error`}>
        <p className="text-lg leading-relaxed text-v3-ink">{img.alt}</p>
        <p className="flex items-center gap-2 text-base leading-relaxed text-v3-muted"><ImageOff className="h-5 w-5 shrink-0" aria-hidden="true" />Das Bild konnte nicht geladen werden.</p>
        <button type="button" onClick={() => retry((n) => n + 1)} aria-label={`Bild erneut laden: ${img.alt}`}
          className="inline-flex min-h-[48px] items-center rounded-lg border border-brand-purple/30 px-4 text-lg font-semibold text-brand-purple underline underline-offset-4">{t("common.retry")}</button>
      </figcaption>
    </figure>
  );
  return (
    <figure className="mt-4 overflow-hidden rounded-[12px] border border-v3-line bg-v3-lilac" data-testid={testid} data-state="loaded">
      <img src={state.src} alt={img.alt} decoding="async" referrerPolicy="no-referrer" className="max-h-[32rem] w-full object-contain" data-testid={`${testid}-img`}
        onError={() => { state.release(); setState((old) => old === state ? { img, version: state.version, failed: true } : old); }} />
    </figure>
  );
}
